import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  InputBase,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import { useNavigate } from "react-router-dom";
import { NavItem, flattenNav } from "./navigation";

const normalise = (value: string) =>
  value
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss");

const matches = (item: NavItem, query: string) => {
  const haystack = normalise(
    [item.title, item.path, ...(item.keywords ?? [])].join(" "),
  );
  return normalise(query)
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => haystack.includes(term));
};

/**
 * ⌘K / Ctrl+K navigator. Filters every destination in the nav model
 * and jumps to the highlighted one on Enter.
 */
export const CommandPalette = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const destinations = useMemo(() => flattenNav(), []);
  const results = useMemo(
    () => (query ? destinations.filter((item) => matches(item, query)) : destinations),
    [destinations, query],
  );

  // Reset between openings so the palette never reopens mid-search.
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
    }
  }, [open]);

  // Keep the highlight inside the result set as it shrinks.
  useEffect(() => {
    setHighlight((current) => Math.min(current, Math.max(results.length - 1, 0)));
  }, [results.length]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    const node = listRef.current?.children[highlight] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const go = (item: NavItem) => {
    navigate(item.path);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlight((current) => (current + 1) % Math.max(results.length, 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlight(
        (current) =>
          (current - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1),
      );
    } else if (event.key === "Enter" && results[highlight]) {
      event.preventDefault();
      go(results[highlight]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            mt: "12vh",
            alignSelf: "flex-start",
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          height: 52,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <SearchIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Seite suchen…"
          sx={{ fontSize: "0.9375rem" }}
          inputProps={{ "aria-label": "Seite suchen" }}
        />
      </Box>

      {results.length ? (
        <List
          ref={listRef}
          disablePadding
          sx={{ maxHeight: 340, overflowY: "auto", p: 1 }}
        >
          {results.map((item, index) => (
            <ListItemButton
              key={`${item.path}-${item.title}`}
              selected={index === highlight}
              onMouseEnter={() => setHighlight(index)}
              onClick={() => go(item)}
              sx={{ borderRadius: 2, gap: 1.5, minHeight: 40 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "text.secondary",
                  "& .MuiSvgIcon-root": { fontSize: "1.125rem" },
                }}
              >
                {item.icon}
              </Box>
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                {item.title}
              </Typography>
              <Typography
                variant="caption"
                sx={{ ml: "auto", color: "text.disabled" }}
              >
                {item.path}
              </Typography>
            </ListItemButton>
          ))}
        </List>
      ) : (
        <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Keine Treffer für „{query}“
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

/** Binds ⌘K / Ctrl+K to a setter, ignoring keystrokes aimed at inputs. */
export const useCommandPaletteHotkey = (onOpen: () => void) => {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) {
        return;
      }
      event.preventDefault();
      onOpen();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onOpen]);
};

export default CommandPalette;
